import * as React from "react";
import { DropDownList, ComboBox } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { formatDate } from "@progress/kendo-intl";
import { process } from "@progress/kendo-data-query";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import FeatureAccessAddClientsModal from "./FeatureAccessAddClientsModal";
import FeatureAccessImportClientsModal from "./FeatureAccessImportClientsModal";
import { useQueryGetOEPeriodList } from "../../data/services/openEnrollmentService";
import {
  useQueryGetFeatureKeys,
  useQueryGetFeatureAccessList,
  useMutatePostFeatureAccessUpdateKey,
  useMutatePostFeatureAccessRemove,
  useMutatePostFeatureAccessRemoveAll,
} from "../../data/services/featureAccessService";

// Feature keys are stored and compared lowercase; see FeatureKeys.Normalize on
// the server. The ComboBox accepts free text, so normalize before querying so a
// typed "FeatureA" finds the rows stored as "featurea".
const normalizeFeatureKey = (raw) =>
  raw === undefined || raw === null ? "" : String(raw).trim().toLowerCase();

// Sentinel for the "show every feature" choice. Empty string is what the API
// treats as unscoped, so the ComboBox needs a distinct visible label.
const ALL_FEATURES = "(all features)";

// Flag the Grid uses to decide whether a row renders as editor or text, matching
// the convention in pages/mapping/PlanMapping.jsx.
const EDIT_FIELD = "inEdit";

/**
 * Feature Access Control admin page.
 *
 * Maintains the opt-in client allow-list for one gated feature within one OE
 * period. A client on this list sees the feature; every other client does not.
 */
const FeatureAccessControl = () => {
  const [selectedOEPeriod, setSelectedOEPeriod] = React.useState(null);
  const [selectedFeatureKey, setSelectedFeatureKey] = React.useState(null);
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = React.useState(false);
  const [gridDataState, setGridDataState] = React.useState({
    skip: 0,
    take: 20,
  });
  // clientId|featureKey of the row being edited, plus its pending value.
  const [editingRowId, setEditingRowId] = React.useState(null);
  const [editingValue, setEditingValue] = React.useState("");
  const [editError, setEditError] = React.useState(null);

  const { isLoading: oePeriodsLoading, data: oePeriods } =
    useQueryGetOEPeriodList();
  const { isLoading: featureKeysLoading, data: featureKeys } =
    useQueryGetFeatureKeys();

  // Memoized: a fresh [] on every render would re-run the default-selection
  // effect below on every render (react-hooks/exhaustive-deps warns about it).
  const featureKeyOptions = React.useMemo(
    () => featureKeys?.data ?? [],
    [featureKeys]
  );
  const featureComboData = [
    ALL_FEATURES,
    ...featureKeyOptions.map((option) => option.featureKey),
  ];

  const oePeriodId = selectedOEPeriod?.oePeriodId;

  const showingAllFeatures = selectedFeatureKey === ALL_FEATURES;

  const { isLoading: listLoading, data: listResponse } =
    useQueryGetFeatureAccessList(
      oePeriodId,
      showingAllFeatures ? "" : selectedFeatureKey,
      includeInactive
    );

  const updateKeyHandler = useMutatePostFeatureAccessUpdateKey();
  const removeHandler = useMutatePostFeatureAccessRemove();
  const removeAllHandler = useMutatePostFeatureAccessRemoveAll();

  React.useEffect(() => {
    if (!oePeriodsLoading && oePeriods?.data?.length > 0 && !selectedOEPeriod) {
      setSelectedOEPeriod(oePeriods.data[0]);
    }
  }, [oePeriodsLoading, oePeriods, selectedOEPeriod]);

  React.useEffect(() => {
    if (
      !featureKeysLoading &&
      featureKeyOptions.length > 0 &&
      !selectedFeatureKey
    ) {
      setSelectedFeatureKey(featureKeyOptions[0].featureKey);
    }
  }, [featureKeysLoading, featureKeyOptions, selectedFeatureKey]);

  const rawRows = listResponse?.data ?? [];
  const rowId = (row) => `${row.clientId}|${row.featureKey}`;
  const rows = rawRows.map((row) => ({
    ...row,
    [EDIT_FIELD]: rowId(row) === editingRowId,
  }));
  const activeRows = rows.filter((row) => row.active);
  const activeEntries = activeRows.map((row) => ({
    clientId: row.clientId,
    featureKey: row.featureKey,
  }));

  // A typed key with no matching constant is legal but gates nothing until a
  // developer reads it, so say so next to the input.
  const selectedIsDefinedInCode =
    showingAllFeatures ||
    featureKeyOptions.some(
      (option) =>
        option.isDefinedInCode &&
        option.featureKey === normalizeFeatureKey(selectedFeatureKey)
    );

  const handleRemove = async (dataItem) => {
    // Use the row's own feature: in the all-features view the selected key is
    // the sentinel, and even when scoped the row is the authoritative source.
    try {
      await removeHandler.mutateAsync({
        oePeriodId,
        featureKey: dataItem.featureKey,
        clientId: dataItem.clientId,
      });
    } catch {
      // Surfaced by <ErrorBanner handler={removeHandler} />.
    }
  };

  const startEdit = (dataItem) => {
    setEditError(null);
    setEditingRowId(rowId(dataItem));
    setEditingValue(dataItem.featureKey);
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditingValue("");
    setEditError(null);
  };

  const saveEdit = async (dataItem) => {
    const nextKey = normalizeFeatureKey(editingValue);

    if (!nextKey) {
      setEditError("Feature name cannot be blank.");
      return;
    }

    let response;

    try {
      response = await updateKeyHandler.mutateAsync({
        oePeriodId,
        clientId: dataItem.clientId,
        currentFeatureKey: dataItem.featureKey,
        newFeatureKey: nextKey,
      });
    } catch {
      // A transport/500 failure must not silently drop the typed value: stay in
      // edit mode with the value intact so the admin can retry.
      setEditError("Could not change the feature. Please try again.");
      return;
    }

    // A rejected change comes back as updated: false with a reason rather than
    // throwing, so keep the row in edit mode and surface the message.
    if (response?.data?.updated === false) {
      setEditError(
        response.data.errorMessage ?? "Could not change the feature."
      );
      return;
    }

    cancelEdit();
  };

  const handleRemoveAll = async () => {
    setShowRemoveAllConfirm(false);

    // Belt and braces: the button is disabled in the all-features view, but a
    // bulk delete is not something to leave guarded by a disabled prop alone.
    if (showingAllFeatures || !selectedFeatureKey) {
      return;
    }

    try {
      await removeAllHandler.mutateAsync({
        oePeriodId,
        featureKey: selectedFeatureKey,
      });
    } catch {
      // Surfaced by <ErrorBanner handler={removeAllHandler} />; swallowed here
      // so the click handler does not reject unhandled.
    }
  };

  if (oePeriodsLoading || featureKeysLoading) {
    return <LoadingIndicator />;
  }

  const removeAllMessage = `This removes all ${
    activeRows.length
  } client(s) from "${selectedFeatureKey}" for ${
    selectedOEPeriod?.oePeriodName ?? "the selected OE period"
  }. Other features and other OE periods are not affected. Continue?`;

  const dateCreatedCell = (props) => {
    // new Date(null) is the epoch and new Date(undefined) is Invalid Date, so
    // an absent value would otherwise render as 01/01/1970 or "Invalid Date".
    const raw = props.dataItem.dateCreated;
    const parsed = raw ? new Date(raw) : null;
    const isValid = parsed !== null && !Number.isNaN(parsed.getTime());

    return <td>{isValid ? formatDate(parsed, "MM/dd/yyyy") : ""}</td>;
  };

  const statusCell = (props) => (
    <td>{props.dataItem.active ? "Active" : "Removed"}</td>
  );

  // Renders the feature name, or a free-text ComboBox while the row is being
  // edited. Editing moves the client's grant to a different feature.
  const featureCell = (props) => {
    if (!props.dataItem[EDIT_FIELD]) {
      return <td>{props.dataItem.featureKey}</td>;
    }

    return (
      <td>
        <ComboBox
          data={featureKeyOptions.map((option) => option.featureKey)}
          value={editingValue}
          allowCustom={true}
          onChange={(e) => setEditingValue(normalizeFeatureKey(e.target.value))}
          style={{ width: "100%" }}
        />
      </td>
    );
  };

  const actionCell = (props) => {
    const dataItem = props.dataItem;

    if (!dataItem.active) {
      return <td />;
    }

    if (dataItem[EDIT_FIELD]) {
      return (
        <td>
          <Button
            themeColor={"success"}
            fillMode={"flat"}
            disabled={updateKeyHandler.isLoading}
            onClick={() => saveEdit(dataItem)}
          >
            {"Save"}
          </Button>
          <Button fillMode={"flat"} onClick={cancelEdit}>
            {"Cancel"}
          </Button>
        </td>
      );
    }

    return (
      <td>
        <Button
          fillMode={"flat"}
          disabled={editingRowId !== null}
          onClick={() => startEdit(dataItem)}
        >
          {"Edit"}
        </Button>
        <Button
          themeColor={"error"}
          fillMode={"flat"}
          disabled={editingRowId !== null}
          onClick={() => handleRemove(dataItem)}
        >
          {"Remove"}
        </Button>
      </td>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Feature Access Control</h2>
      <p>
        Clients on this list can see the selected feature for the selected OE
        period. Every other client cannot.
      </p>

      <ErrorBanner handler={removeHandler} />
      <ErrorBanner handler={removeAllHandler} />
      <SuccessBanner
        handler={removeHandler}
        message={"Client removed from this feature."}
      />
      <SuccessBanner
        handler={removeAllHandler}
        message={"All clients removed from this feature."}
      />
      <SuccessBanner
        handler={updateKeyHandler}
        message={"Feature updated."}
      />
      {editError && (
        <div
          data-testid="edit-error"
          role="alert"
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginBottom: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {editError}
        </div>
      )}

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-end" }}>
        <div>
          <label
            id={"feature-access-feature-label"}
            style={{ display: "block", fontWeight: 500 }}
          >
            Feature
          </label>
          <ComboBox
            data={featureComboData}
            value={selectedFeatureKey}
            allowCustom={true}
            ariaLabelledBy={"feature-access-feature-label"}
            placeholder={"Select or type a feature name"}
            onChange={(e) => {
              const next = e.target.value;
              cancelEdit();
              setSelectedFeatureKey(
                next === ALL_FEATURES ? ALL_FEATURES : normalizeFeatureKey(next)
              );
            }}
            style={{ width: "260px" }}
          />
          {selectedFeatureKey && !selectedIsDefinedInCode && (
            <div
              data-testid="unknown-feature-note"
              style={{ fontSize: "12px", color: "#856404", maxWidth: "260px" }}
            >
              Not defined in code &mdash; saved, but no screen reads it yet.
            </div>
          )}
        </div>
        <div>
          <label style={{ display: "block", fontWeight: 500 }}>OE Period</label>
          <DropDownList
            data={oePeriods?.data ?? []}
            ariaLabel={"OE Period"}
            textField={"oePeriodName"}
            dataItemKey={"oePeriodId"}
            value={selectedOEPeriod}
            onChange={(e) => setSelectedOEPeriod(e.target.value)}
            style={{ width: "260px" }}
          />
        </div>
        <div>
          <Checkbox
            label={"Show removed"}
            value={includeInactive}
            onChange={(e) => setIncludeInactive(e.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        {listLoading ? (
          <LoadingIndicator />
        ) : (
          <Grid
            data={process(rows, gridDataState)}
            {...gridDataState}
            onDataStateChange={(event) => setGridDataState(event.dataState)}
            sortable={true}
            filterable={true}
            pageable={{ pageSizes: [20, 50, 100] }}
          >
            <GridToolbar>
              <Button
                themeColor={"primary"}
                disabled={!selectedFeatureKey || showingAllFeatures}
                onClick={() => setShowAddModal(true)}
              >
                {"Add Clients"}
              </Button>
              <Button
                disabled={!selectedFeatureKey || showingAllFeatures}
                onClick={() => setShowImportModal(true)}
              >
                {"Import"}
              </Button>
              {/* Disabled in the all-features view: one click would otherwise
                  wipe every feature for the period. */}
              <Button
                themeColor={"error"}
                disabled={activeRows.length === 0 || showingAllFeatures}
                onClick={() => setShowRemoveAllConfirm(true)}
              >
                {"Remove All"}
              </Button>
              {showingAllFeatures && (
                <span style={{ marginLeft: 8, color: "#666", fontSize: "12px" }}>
                  Pick a single feature to add, import, or remove all.
                </span>
              )}
            </GridToolbar>
            <Column field="clientId" title="Client Id" width={140} />
            <Column field="clientName" title="Client Name" />
            <Column
              field="featureKey"
              title="Feature"
              width={220}
              cell={featureCell}
            />
            <Column
              field="dateCreated"
              title="Date Added"
              width={140}
              cell={dateCreatedCell}
            />
            <Column field="createdBy" title="Added By" width={180} />
            {includeInactive && (
              <Column
                field="active"
                title="Status"
                width={120}
                cell={statusCell}
              />
            )}
            <Column title="Actions" width={180} cell={actionCell} />
          </Grid>
        )}
      </div>

      {showAddModal && (
        <FeatureAccessAddClientsModal
          oePeriodId={oePeriodId}
          featureKey={selectedFeatureKey}
          onClose={() => setShowAddModal(false)}
          onSubmit={() => setShowAddModal(false)}
        />
      )}

      {showImportModal && (
        <FeatureAccessImportClientsModal
          oePeriodId={oePeriodId}
          featureKey={selectedFeatureKey}
          currentEntries={activeEntries}
          knownFeatureKeys={featureKeyOptions}
          onClose={() => setShowImportModal(false)}
          onSubmit={() => setShowImportModal(false)}
        />
      )}

      {showRemoveAllConfirm && (
        <ConfirmationDialog
          title={"Remove all clients?"}
          message={removeAllMessage}
          onConfirm={handleRemoveAll}
          onCancel={() => setShowRemoveAllConfirm(false)}
        />
      )}
    </div>
  );
};

export default FeatureAccessControl;
